/**
 * Authentication Utilities
 * Client-only — employee auth is handled by the standalone Employee Portal.
 */

export type AuthTheme = "client" | "mechagora";
export type Audience = "client" | "employee";

export interface AuthConfig {
  loginEndpoint: string;
  redirectTo: string;
  mfaRedirectTo: string;
  branding: {
    title: string;
    subtitle: string;
    theme: AuthTheme;
    colors: {
      primary: string;
      secondary: string;
      accent: string;
    };
  };
}

/**
 * Auth configuration for client or employee audience.
 */
export function getAuthConfig(audience: Audience = "client"): AuthConfig {
  if (audience === "employee") {
    return {
      loginEndpoint: "/api/auth/employee-login",
      redirectTo: "/dashboard",
      mfaRedirectTo: "/auth/mfa/provisional-setup",
      branding: {
        title: "Employee Access",
        subtitle: "Sign in to your enterprise account",
        theme: "mechagora",
        colors: {
          primary: "oklch(0.541 0.104 84)",
          secondary: "oklch(0.496 0.090 84)",
          accent: "oklch(0.678 0.123 62)",
        },
      },
    };
  }

  return {
    loginEndpoint: "/api/auth/zitadel/client/login",
    redirectTo: "/client/dashboard",
    mfaRedirectTo: "/auth/mfa/provisional-setup",
    branding: {
      title: "Client Access",
      subtitle: "Welcome to your personal trading dashboard",
      theme: "mechagora",
      colors: {
        primary: "oklch(0.541 0.104 84)",
        secondary: "oklch(0.496 0.090 84)",
        accent: "oklch(0.678 0.123 62)",
      },
    },
  };
}

/**
 * Clear all auth state from browser storage.
 */
export function clearAllSessions(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("clientSessionToken");
    localStorage.removeItem("userRole");
  }
}

/**
 * Format error message for display.
 */
export function formatAuthError(error: string | null): string {
  if (!error) {
    return "An unexpected error occurred. Please try again.";
  }

  const errorPatterns: Record<string, string> = {
    "Invalid credentials": "Invalid email or password. Please check and try again.",
    "Network error": "Network error. Please check your internet connection.",
    "Server error": "Server error. Please try again later.",
    "Account is not active": "Your account is not active. Please contact support.",
  };

  if (error in errorPatterns) {
    return errorPatterns[error]!;
  }

  for (const [pattern, message] of Object.entries(errorPatterns)) {
    if (error.toLowerCase().includes(pattern.toLowerCase())) {
      return message;
    }
  }

  return error;
}

/**
 * Generate security audit log entry.
 * Supports both (action, userId?, success?, details?) and legacy
 * (action, audience, userId?, success?, details?) call shapes.
 */
export function generateAuditLog(
  action: string,
  userIdOrAudience?: string,
  successOrUserId?: boolean | string,
  detailsOrSuccess?: any,
  maybeDetails?: any
): void {
  let userId = "anonymous";
  let success = true;
  let details: any;

  if (typeof successOrUserId === "boolean") {
    userId = userIdOrAudience || "anonymous";
    success = successOrUserId;
    details = detailsOrSuccess;
  } else {
    // legacy: (action, audience, userId?, success?, details?)
    userId =
      typeof successOrUserId === "string" && successOrUserId
        ? successOrUserId
        : userIdOrAudience || "anonymous";
    success = typeof detailsOrSuccess === "boolean" ? detailsOrSuccess : true;
    details = maybeDetails ?? (typeof detailsOrSuccess === "object" ? detailsOrSuccess : undefined);
  }

  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    action,
    userId,
    success,
    details,
    userAgent: typeof window !== "undefined" ? navigator.userAgent : "server",
    ip: "unknown",
  };

  console.log("Security Audit:", JSON.stringify(logEntry, null, 2));
}

/**
 * Best-effort client-side session presence check (cookie/localStorage).
 * Server BFF remains authoritative.
 */
export function isUserAuthenticated(audience: Audience = "client"): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (audience === "employee") {
      return Boolean(
        localStorage.getItem("employeeSessionToken") ||
          document.cookie.includes("employee_session") ||
          document.cookie.includes("app_session_employee")
      );
    }
    return Boolean(
      localStorage.getItem("clientSessionToken") ||
        localStorage.getItem("sessionToken") ||
        document.cookie.includes("client_session") ||
        document.cookie.includes("app_session")
    );
  } catch {
    return false;
  }
}

export interface SessionContaminationResult {
  hasContamination: boolean;
  contaminationType?: string;
  conflictingSessions: string[];
}

/**
 * Detect mixed client + employee browser session state (hard isolation guard).
 */
export function checkSessionContamination(): SessionContaminationResult {
  if (typeof window === "undefined") {
    return { hasContamination: false, conflictingSessions: [] };
  }
  const client = isUserAuthenticated("client");
  const employee = isUserAuthenticated("employee");
  if (client && employee) {
    return {
      hasContamination: true,
      contaminationType: "client_and_employee",
      conflictingSessions: ["client", "employee"],
    };
  }
  return { hasContamination: false, conflictingSessions: [] };
}

export interface SessionCleanupResult {
  success: boolean;
  cleanedItems: string[];
}

/**
 * Clear mixed session storage keys when contamination is detected.
 */
export function cleanupSessionContamination(): SessionCleanupResult {
  const cleanedItems: string[] = [];
  if (typeof window === "undefined") {
    return { success: true, cleanedItems };
  }
  try {
    const keys = [
      "sessionToken",
      "clientSessionToken",
      "employeeSessionToken",
      "userRole",
      "mfaUserId",
    ];
    for (const key of keys) {
      if (localStorage.getItem(key) != null) {
        localStorage.removeItem(key);
        cleanedItems.push(`localStorage:${key}`);
      }
    }
    clearAllSessions();
    return { success: true, cleanedItems };
  } catch {
    return { success: false, cleanedItems };
  }
}
